import React, { useState } from "react";
import { observer } from "mobx-react-lite";
import {
  Button,
  Card,
  Dropdown,
  Grid,
  Header,
  Icon,
  Image,
  Input,
  Label,
  Loader,
  Message,
  Segment,
} from "semantic-ui-react";
import { useStore } from "../../app/stores/store";
import { Link } from "react-router-dom";
import { sportOptions } from "../../app/common/options/sportOptions";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroller";
import { TournamentSearchParams } from "../../app/models/Tournament";
import ReactSelect from "react-select";

export const TournamentSearchPage = observer(function TournamentSearchPage({
  embedded = false,
}: {
  embedded?: boolean;
}) {
  const { tournamentStore } = useStore();
  const { t } = useTranslation();

  const [nameQuery, setNameQuery] = useState("");
  const [sportId, setSportId] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [formatFilter, setFormatFilter] = useState("");
  const [loadingNext, setLoadingNext] = useState(false);
  const [activeSearchParams, setActiveSearchParams] =
    useState<TournamentSearchParams>({});

  const localizedSports = [
    { value: "", label: t("teamSearch.allSports") },
    ...sportOptions.map((o: { value: string; label: string }) => ({
      value: o.value,
      label: t(`sports.${o.label.toLowerCase()}`) || o.label,
    })),
  ];

  const statusOptions = [
    { key: "", value: "", text: t("tournament.search.allStatuses") },
    ...[
      "RegistrationOpen",
      "Published",
      "InProgress",
      "Completed",
      "Cancelled",
    ].map((s) => ({
      key: s,
      value: s,
      text: t(`tournament.status.${s.toLowerCase()}`),
    })),
  ];

  const formatOptions = [
    { key: "", value: "", text: t("tournament.search.allFormats") },
    ...[
      "SingleElimination",
      "DoubleElimination",
      "RoundRobin",
      "GroupStageKnockout",
    ].map((f) => ({
      key: f,
      value: f,
      text: t(`tournament.format.${f.toLowerCase()}`),
    })),
  ];

  const handleSearch = () => {
    const params: TournamentSearchParams = {
      name: nameQuery || undefined,
      sportId: sportId || undefined,
      status: (statusFilter as any) || undefined,
      format: (formatFilter as any) || undefined,
    };
    tournamentStore.resetTournamentSearch();
    setActiveSearchParams(params);
    tournamentStore.searchTournaments(params);
  };

  const handleGetNext = () => {
    setLoadingNext(true);
    tournamentStore.setTournamentPagingParams(
      tournamentStore.pagination!.currentPage + 1,
    );
    tournamentStore
      .searchTournaments(activeSearchParams, true)
      .then(() => setLoadingNext(false));
  };

  const formatIcons: Record<string, string> = {
    SingleElimination: "sitemap",
    DoubleElimination: "code branch",
    RoundRobin: "sync",
    GroupStageKnockout: "th",
  };

  const statusColors: Record<string, string> = {
    Draft: "grey",
    Published: "blue",
    RegistrationOpen: "green",
    RegistrationClosed: "orange",
    InProgress: "teal",
    Completed: "purple",
    Cancelled: "red",
  };

  const hasMore =
    !loadingNext &&
    !!tournamentStore.pagination &&
    tournamentStore.pagination.currentPage <
      tournamentStore.pagination.totalPages;

  const content = (
    <>
      <Segment>
        <div
          style={{
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            alignItems: "flex-end",
          }}
        >
          <div style={{ flex: "2 1 200px", minWidth: 160 }}>
            <label
              style={{
                display: "block",
                marginBottom: 4,
                fontWeight: 600,
                fontSize: "0.9em",
              }}
            >
              {t("tournament.search.nameLabel")}
            </label>
            <Input
              fluid
              icon="search"
              placeholder={t("tournament.search.namePlaceholder")}
              value={nameQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setNameQuery(e.target.value)
              }
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
                e.key === "Enter" && handleSearch()
              }
            />
          </div>
          <div style={{ flex: "1 1 140px" }}>
            <label
              style={{
                display: "block",
                marginBottom: 4,
                fontWeight: 600,
                fontSize: "0.9em",
              }}
            >
              {t("tournament.search.sportLabel")}
            </label>
            <ReactSelect
              options={localizedSports}
              value={
                localizedSports.find((o) => o.value === sportId) ||
                localizedSports[0]
              }
              onChange={(opt) => setSportId(opt?.value || "")}
              placeholder={t("teamSearch.allSports")}
              isSearchable
              styles={{
                control: (base) => ({ ...base, minHeight: 38 }),
                menu: (base) => ({ ...base, zIndex: 10 }),
              }}
            />
          </div>
          <div style={{ flex: "1 1 140px" }}>
            <label
              style={{
                display: "block",
                marginBottom: 4,
                fontWeight: 600,
                fontSize: "0.9em",
              }}
            >
              {t("tournament.search.statusLabel")}
            </label>
            <Dropdown
              fluid
              selection
              options={statusOptions}
              value={statusFilter}
              onChange={(_: React.SyntheticEvent, d: any) =>
                setStatusFilter(d.value as string)
              }
            />
          </div>
          <div style={{ flex: "1 1 140px" }}>
            <label
              style={{
                display: "block",
                marginBottom: 4,
                fontWeight: 600,
                fontSize: "0.9em",
              }}
            >
              {t("tournament.search.formatLabel")}
            </label>
            <Dropdown
              fluid
              selection
              options={formatOptions}
              value={formatFilter}
              onChange={(_: React.SyntheticEvent, d: any) =>
                setFormatFilter(d.value as string)
              }
            />
          </div>
          <div style={{ flex: "0 0 auto", alignSelf: "flex-end" }}>
            <Button
              primary
              onClick={handleSearch}
              loading={tournamentStore.searching}
              disabled={tournamentStore.searching}
            >
              <Icon name="search" />
              {t("tournament.search.search")}
            </Button>
          </div>
        </div>
      </Segment>

      {tournamentStore.searching && !loadingNext ? (
        <Segment basic textAlign="center" style={{ padding: 40 }}>
          <Loader active inline="centered" size="medium" />
        </Segment>
      ) : tournamentStore.searchResults.length === 0 ? (
        <Message info>
          <Icon name="search" />
          {t("tournament.search.startSearch")}
        </Message>
      ) : (
        <>
          <p style={{ color: "#666", marginBottom: 10 }}>
            {t("tournament.search.resultsCount", {
              count:
                tournamentStore.pagination?.totalItems ??
                tournamentStore.searchResults.length,
            })}
          </p>
          <InfiniteScroll
            pageStart={0}
            loadMore={handleGetNext}
            hasMore={hasMore}
            initialLoad={false}
            useWindow={true}
          >
            <Card.Group stackable itemsPerRow={3}>
              {tournamentStore.searchResults.map((tournament: any) => (
                <Card
                  key={tournament.id}
                  as={Link}
                  to={`/tournaments/${tournament.id}`}
                >
                  <Card.Content>
                    <Image
                      floated="right"
                      size="mini"
                      src={
                        tournament.logoUrl ||
                        "/assets/tournament-placeholder.png"
                      }
                      rounded
                      style={{ width: 44, height: 44, objectFit: "cover" }}
                    />
                    <Card.Header>{tournament.name}</Card.Header>
                    <Card.Meta>{t(`sports.${tournament.sportName.toLowerCase()}`) || tournament.sportName}</Card.Meta>
                    <Card.Description>
                      <div
                        style={{
                          display: "flex",
                          gap: 6,
                          flexWrap: "wrap",
                          marginTop: 6,
                        }}
                      >
                        <Label
                          size="tiny"
                          color={statusColors[tournament.status] as any}
                        >
                          {t(
                            `tournament.status.${tournament.status.toLowerCase()}`,
                          )}
                        </Label>
                        <Label size="tiny" basic>
                          <Icon name={formatIcons[tournament.format] as any} />
                          {t(
                            `tournament.format.${tournament.format.toLowerCase()}`,
                          )}
                        </Label>
                        {tournament.isOnline && (
                          <Label size="tiny" color="blue" basic>
                            <Icon name="wifi" />
                            Online
                          </Label>
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: "0.82em",
                          color: "#888",
                          marginTop: 6,
                        }}
                      >
                        <Icon name="calendar" />
                        {new Date(tournament.startsAt).toLocaleDateString()}
                        <span style={{ marginLeft: 10 }}>
                          <Icon name="users" />
                          {tournament.registeredTeamsCount}/
                          {tournament.maxTeams}
                        </span>
                      </div>
                    </Card.Description>
                  </Card.Content>
                  <Card.Content extra>
                    <span style={{ fontSize: "0.82em" }}>
                      <Icon name="user" />
                      {tournament.organizerName}
                      {tournament.entryFee === 0 && (
                        <span style={{ marginLeft: 10, color: "#21ba45" }}>
                          🆓 Free
                        </span>
                      )}
                      {tournament.entryFee > 0 && (
                        <span style={{ marginLeft: 10 }}>
                          €{tournament.entryFee}
                        </span>
                      )}
                    </span>
                  </Card.Content>
                </Card>
              ))}
            </Card.Group>
          </InfiniteScroll>
          {loadingNext && (
            <Segment basic textAlign="center" style={{ padding: 20 }}>
              <Loader active inline="centered" size="small" />
            </Segment>
          )}
        </>
      )}
    </>
  );

  if (embedded) return <div style={{ padding: "14px 0 0" }}>{content}</div>;

  return (
    <Grid stackable>
      <Grid.Column width={16}>
        <Segment color="teal">
          <Header as="h2">
            <Icon name="trophy" color="teal" />
            <Header.Content>
              {t("tournament.search.title")}
              <Header.Subheader>
                {t("tournament.search.subtitle")}
              </Header.Subheader>
            </Header.Content>
          </Header>
        </Segment>
        {content}
      </Grid.Column>
    </Grid>
  );
});
